import {noop} from '@benchling/js-utils';

import {isBiologicsEnabled} from 'antibodies/util/biologicsCapability';
import BenchlingConfig from 'BenchlingConfig';
import {ProteinAlignmentKind, AaSequenceAlignmentRow} from 'constants/AlignmentConstants';
import {makeSliceView} from 'molbio/aa-sequences/alignments/AaSequenceAlignmentUtil';
import * as ProteinProperties from 'molbio/aa-sequences/util/AaSequenceProperties';
import {listenToAntibodyVisibilitySettings} from 'molbio/antibody-engineering/visibilityHelpers';
import {AlignmentRowInfoWrapperProps} from 'molbio/maps/alignment/AlignmentRowInfo';
import PolymerAlignmentRowView, {
  PolymerAlignmentRowViewInitializationOptions,
} from 'molbio/maps/alignment/PolymerAlignmentRowView';
import Slice from 'molbio/maps/linear/Slice';
import {SliceView} from 'molbio/maps/linear/SliceView';
import Range from 'molbio/maps/selectables/Range';
import {Selection} from 'molbio/maps/selectables/Selection';
import * as sequtil from 'molbio/SeqUtil';
import AaSequenceAlignmentTabState from 'molbio/tabstates/AaSequenceAlignmentTabState';
import {VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import * as bclip from 'util/BenchlingClipboard';

interface AaSequenceAlignmentRowViewInitOptions extends PolymerAlignmentRowViewInitializationOptions<
  AaSequenceAlignmentTabState,
  AaSequenceAlignmentTabState
> {
  alignmentType?: ProteinAlignmentKind;
  row: AaSequenceAlignmentRow;
  visibilityOptions: VisibilityOptions;
}

export default class AaSequenceAlignmentRowView extends PolymerAlignmentRowView<
  AaSequenceAlignmentTabState,
  AaSequenceAlignmentTabState
> {
  alignmentRowType!: ProteinAlignmentKind | null;
  mapstate = null; // for protein alignments, there is no mapstate model, just the tabstate
  row!: AaSequenceAlignmentRow;
  override tabstate!: AaSequenceAlignmentTabState;

  constructor(options: AaSequenceAlignmentRowViewInitOptions) {
    super(options);
  }

  override initialize(options: AaSequenceAlignmentRowViewInitOptions): void {
    super.initialize(options);
    this.row = options.row;
    this.alignmentRowType = options.alignmentType || null; // null for every row except the top row.
    this.tabstate = this.model; // for protein alignments, there is no mapstate model, just the tabstate
    if (BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS && !isBiologicsEnabled()) {
      listenToAntibodyVisibilitySettings(this.visibilityOptions, this, () => {
        this.renderRowMetadata();
      });
    }
    this.listenTo(this.visibilityOptions.getOption(vo.ALIGNMENT_ROW_STATS.name), 'change:visible', () => {
      this.renderRowMetadata();
    });
  }

  getRange(): Range {
    return this.model.get('range');
  }

  getAlignmentRowType(): ProteinAlignmentKind | null {
    return this.alignmentRowType;
  }

  getRowContentProps(): AlignmentRowInfoWrapperProps {
    const alignment = this.model.get('alignment')!;
    const alignmentType = alignment.alignmentType;
    const templateResidues =
      alignmentType === ProteinAlignmentKind.TEMPLATE
        ? alignment.rows[0].proteinVersion.aminoAcids
        : undefined;
    return {
      alignmentRowType: this.getAlignmentRowType() ?? undefined,
      canEdit: false,
      onDeleteRow: noop,
      options: this.visibilityOptions,
      // Protein alignments are read-only, so this count cannot update unless the entire alignment updates.
      featureCounts: {annotations: this.row.proteinVersion.annotations.length},
      rowInfoProps: {
        residues: this.row.proteinVersion.aminoAcids,
        visibilityOptions: this.visibilityOptions,
        name: this.row.name,
        canEdit: false,
        isAaSequenceAlignment: true,
        alignmentType,
        isReferenceRow: Boolean(this.getAlignmentRowType()),
        templateResidues,
      },
    };
  }

  getSlice(): Slice {
    return this.model.get('slice');
  }

  makeSliceView(): SliceView {
    return makeSliceView({
      alignmentRowType: this.alignmentRowType,
      tabState: this.tabstate,
      slice: this.tabstate.get('slice'),
      visibilityOptions: this.visibilityOptions,
      row: this.row,
      forAlignmentPNGExport: false,
    });
  }

  getSelection(): Selection {
    return this.model.get('selection');
  }

  getResidueSlice(start: number, end?: number): string {
    return sequtil.residueSlice(this.row.proteinVersion.aminoAcids, start, end, false);
  }

  handlePasteText(text: string): void {
    throw new Error('Unexpected call to handlePasteText: protein alignments are read-only');
  }

  handleCtrlShiftC(e: Event): void {
    // Proteins don't have a special copy menu. We prevent default behavior so the
    // element-select browser behavior in chrome doesn't trigger.
    // TODO: Code re-use with ProteinMapView
    e.preventDefault();
  }

  isValidCharacter(char: string): boolean {
    // TODO: Code re-use with ProteinMapView
    return ProteinProperties.areAminoAcidsValid(char);
  }

  showInputBubble(options?: unknown): void {
    throw new Error('Unexpected call to showInputBubble: protein alignments are read-only');
  }

  storeRegionInBenchlingClipboard(e: JQueryEventObject): string {
    const selection = this.getSelection();
    const aminoAcids = this.getResidueSlice(selection.start(), selection.end() ?? undefined);
    bclip.clipboard.store(aminoAcids);
    const clipboardDesc = `${this.row.proteinVersion.name}: ${selection.start()}-${selection.end()}aa`;
    bclip.clipboard.store(aminoAcids);
    return bclip.clipboard.getHash(aminoAcids, clipboardDesc);
  }

  override handleContextMenu(e: JQueryMouseEventObject): void {
    // Protein alignments are read-only so don't show the context menu.
    e.preventDefault();
  }

  undo(): void {
    // Protein alignments are read-only.
    noop();
  }
}

AaSequenceAlignmentRowView.initClass();
